import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { SectionList, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView, withAnimatedNavigationHeader, SCREEN_BOTTOM_PADDING } from 'design-system';
import Spacer from 'design-system/Spacer';
import { getBankConnection } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectConnectionById } from 'reducers/selectors';
import StatusBar from 'design-system/StatusBar';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import SectionHeader from 'design-system/SectionHeader';
import ListLoader from 'design-system/ListLoader';
import { useHideAccount } from 'features/accounts/hooks/useHideAccount';
import { useCloseAccount } from 'features/accounts/hooks/useCloseAccount';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { useFocusEffect } from 'hooks';
import { BankConnectionAccount } from './components/BankConnectionAccount';
import { isWeb } from '../../../constants';
const keyExtractor = (item, index) => (item.id === -1 ? index : item.id).toString();
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);
const ClosedAccountsScreenContent = ({ connection, navigation, onScroll }) => {
    const ListHeaderComponent = useMemo(() => (<ScreenInfoHeader subTitle="Closed accounts no longer sync, but their historical data remains visible across the app unless you hide them." style={styles.subtitle}/>), []);
    const renderSectionHeader = useCallback(({ section }) => {
        if (!section.data.length || !section.title)
            return null;
        return (<SectionHeader horizontalMargins largeVerticalMargins toolTipTitle={section.toolTipTitle} toolTipSubTitle={section.toolTipSubTitle}>
        {section.title}
      </SectionHeader>);
    }, []);
    const handleToggleClosed = useCloseAccount();
    const handleToggleHidden = useHideAccount();
    const renderItem = useCallback(({ item }) => (<BankConnectionAccount account={item} isConnectionClosed={connection.isClosed} isConnectionDeactivated={!!connection.deactivatedOverQuota} toggleClosed={() => handleToggleClosed(item)} toggleHidden={() => handleToggleHidden(item)}/>), [connection.isClosed, connection.deactivatedOverQuota, handleToggleClosed, handleToggleHidden]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const syncingAccounts = useAppSelector((store) => store.linkBank.syncingAccounts?.[connection.id]);
    const sections = useMemo(() => {
        const closedAccounts = (syncingAccounts || connection.allAccounts).filter((account) => account.isClosed);
        if (!closedAccounts.length)
            return [];
        const accounts = [];
        const hiddenAccounts = [];
        closedAccounts.forEach((account) => {
            if (account.isHidden) {
                hiddenAccounts.push(account);
            }
            else {
                accounts.push(account);
            }
        });
        return [
            { data: accounts, title: 'Accounts' },
            {
                data: hiddenAccounts,
                title: 'Hidden accounts',
                toolTipTitle: 'What are hidden accounts?',
                toolTipSubTitle: 'Hidden accounts’ balances and transactions aren’t visible in the app',
            },
        ];
    }, [connection.allAccounts, syncingAccounts]);
    useFocusEffect(useCallback(() => {
        if (sections.length)
            return;
        if (navigation.canGoBack()) {
            navigation.pop();
        }
        else {
            navigation.navigate('BankConnection', { connectionId: connection.id }, { pop: true });
        }
    }, [connection.id, sections.length]));
    return (<AppView type="secondary" withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <AnimatedSectionList contentContainerStyle={contentContainerStyle} sections={sections} renderItem={renderItem} stickySectionHeadersEnabled={false} renderSectionHeader={renderSectionHeader} ItemSeparatorComponent={Spacer.H16} keyExtractor={keyExtractor} ListHeaderComponent={ListHeaderComponent} onScroll={onScroll}/>
    </AppView>);
};
const ClosedAccountsScreen = withAnimatedNavigationHeader(({ navigation, route, onScroll }) => {
    const { connectionId } = route.params;
    const dispatch = useAppDispatch();
    const connection = useAppSelector((store) => selectConnectionById(store, connectionId));
    useLayoutEffect(() => {
        navigation.setOptions({
            title: connection?.bankInfo.name || 'Bank connection',
        });
    }, [connection, connection?.bankInfo.name]);
    useEffect(() => {
        dispatch(getBankConnection(connectionId));
    }, [connectionId]);
    if (!connection)
        return <ListLoader />;
    return (<ClosedAccountsScreenContent navigation={navigation} route={route} connection={connection} onScroll={onScroll}/>);
}, ({ colors }) => ({
    title: 'Closed accounts',
    headerTintColor: colors.text.primary,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styles = StyleSheet.create({
    subtitle: {
        paddingBottom: 0,
    },
});
export default ClosedAccountsScreen;
